/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import { IconDownload, IconSearch, IconX, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ListView from "../../../components/common/ListView";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Tooltip from "../../../components/common/Tooltip";
import useDebounce from "../../../utilities/useDebounce";
import { useShipmentInventoryListData } from "../../../hooks/inventoryHooks";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";

interface InventoryTabProps {
  headerSubTabs?: React.ReactNode;
}

export default function InventoryTab({ headerSubTabs }: InventoryTabProps = {}) {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const debouncedSearchFor = useDebounce(searchFor, 550);

  const profile = useSelector((state: any) => state.auth.profile);
  const warehouseId = profile?.warehouse;

  const { data, isLoading, isFetching, error, refetch } =
    useShipmentInventoryListData(pageNo, limit, debouncedSearchFor, warehouseId);

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor, warehouseId]);

  const totalPages = data?.totalPages ?? 0;

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setPageNo(page);
  };

  const renderPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (pageNo > 3) pages.push("...");
      for (let i = Math.max(2, pageNo - 1); i <= Math.min(totalPages - 1, pageNo + 1); i++) {
        pages.push(i);
      }
      if (pageNo < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const handleDownloadXLSX = () => {
    if (!data?.inventoryMovement || data.inventoryMovement.length === 0) {
      return;
    }
    const visibleColumns = columnsData.filter((col) => col.isShow && col.value !== "status");
    const exportData = data.inventoryMovement.map((item: any, index: number) => {
      const row: any = {};
      visibleColumns.forEach((col) => {
        if (col.value === "sn") {
          row[col.text] = (pageNo - 1) * limit + (index + 1);
        } else if (col.value === "awb") {
          row["AWB"] = item.awb || "";
        } else if (col.value === "referenceNumber") {
          row["Reference ID"] = item.referenceNumber || "";
        } else if (col.value === "clientName") {
          row["Client"] = item.clientName || "";
        } else if (col.value === "origin") {
          row["Origin"] = item.origin || "";
        } else if (col.value === "destination") {
          row["Country"] = item.receiverCountry || "";
          row["City"] = item.receiverCity || "";
          row["District"] = item.receiverDistrict || "";
        } else if (col.value === "shelfId") {
          row["Location ID"] = item.locationName || "";
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory");
    XLSX.writeFile(wb, "shipment_inventory.xlsx");
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "AWB", value: "awb", isShow: true },
    { text: "Reference ID", value: "referenceNumber", isShow: true },
    { text: "Client", value: "clientName", isShow: true },
    { text: "Origin", value: "origin", isShow: true },
    { text: "Destination", value: "destination", isShow: true },
    { text: "Location ID", value: "shelfId", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col
      )
    );
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "sn":
          return {
            label: "#",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "awb":
          return {
            label: "AWB",
            render: (c: any) => c.awb,
          };
        case "referenceNumber":
          return {
            label: "Reference ID",
            render: (c: any) => c.referenceNumber,
          };
        case "clientName":
          return {
            label: "Client",
            render: (c: any) => c.clientName,
          };
        case "origin":
          return {
            label: "Origin",
            render: (c: any) => c.origin,
          };
        case "destination":
          return {
            label: "Destination",
            render: (c: any) => (
              <div className="flex flex-col text-left">
                {/* City + Country */}
                <div
                  className="text-xs font-bold text-[#212121] truncate"
                  title={`${c.receiverCity || ""}${c.receiverCountry ? `, ${c.receiverCountry}` : ""}`}
                >
                  {c.receiverCity || ""}
                  {c.receiverCountry ? `, ${c.receiverCountry}` : ""}
                </div>

                {/* District */}
                {c.receiverDistrict && (
                  <div className="text-[9px] text-slate-400 font-bold flex items-center gap-1 mt-1">
                    <span
                      className="truncate"
                      title={`Dist: ${c.receiverDistrict}`}
                    >
                      Dist: {c.receiverDistrict}
                    </span>
                  </div>
                )}
              </div>
            ),
          };
        case "shelfId":
          return {
            label: "Location ID",
            render: (c: any) => c.locationName,
          };
        case "status":
          return {
            label: "Status",
            render: (c: any) => {
              return (
                <div className="flex justify-left">
                  <span className="px-2.5 py-1 rounded-lg text-xs font-semibold border bg-slate-100 text-slate-800 border-slate-200">
                    Shelved
                  </span>
                </div>
              );
            },
          };
        default:
          return { label: "", render: () => null };
      }
    });

  return (
    <>
      {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
      <div className="relative grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-full rounded-r-3xl">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="col-span-12 z-10">
          <div className="rounded-3xl overflow-visible" style={{ minHeight: "150px" }}>
            <div className="px-6 py-1.5">
          {/* Header Row (Filters + Actions Unified) */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left side: Badge */}
            <div className="flex flex-wrap items-center gap-3">
              {headerSubTabs}
            </div>

            {/* Right side: Actions & Controls */}
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Search */}
              <div className="relative flex items-center group">
                <div
                  onClick={() => !showSearch && setShowSearch(true)}
                  className={`flex items-center transition-all duration-300 ease-in-out border border-[#E4E5E7] rounded-lg bg-white shadow-sm h-8 ${showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150"}`}
                >
                  <button onClick={(e) => { e.stopPropagation(); setShowSearch(!showSearch); if (showSearch) { setSearchFor(""); setPageNo(1); } }} className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer">
                    <IconSearch size={16} />
                  </button>
                  {showSearch && (
                    <>
                      <input type="text" value={searchFor} onChange={(e) => { setSearchFor(e.target.value); setPageNo(1); }}
                        placeholder="Search..." autoFocus
                        className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                      />
                      <button onClick={(e) => { e.stopPropagation(); setSearchFor(""); setShowSearch(false); }}
                        className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0 cursor-pointer"
                      >
                        <IconX size={14} stroke={2.5} />
                      </button>
                    </>
                  )}
                </div>
                {!showSearch && (
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                    Search
                  </div>
                )}
              </div>

              {/* Export/Download */}
              <div className="relative group">
                <button
                  onClick={handleDownloadXLSX}
                  className="h-8 w-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                >
                  <IconDownload size={18} stroke={2.5} />
                </button>
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                  Export to Excel
                </div>
              </div>

              {/* Entries Limit Selector */}
              {data && totalPages * limit > 10 && (
                <div className="relative group">
                  <EntriesSelector
                    value={limit}
                    options={[
                      10,
                      ...(totalPages * limit > 10 ? [20] : []),
                      ...(totalPages * limit > 20 ? [30] : []),
                      ...(totalPages * limit > 50 ? [50] : []),
                      ...(totalPages * limit > 100 ? [100] : []),
                    ]}
                    onChange={(val) => {
                      setLimit(val);
                      setPageNo(1);
                    }}
                  />
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                    Entries Limit
                  </div>
                </div>
              )}

              {/* Columns Hide/Show Filter */}
              <div className="relative group">
                <ColumnsFilter
                  triggerClassName="border border-slate-200 bg-white rounded-lg p-1.5 cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
                  columnsData={columnsData}
                  setHeadings={setHeadings}
                />
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                  Columns View
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Container (Joined) */}
        <div className="border-t border-slate-100">
          {isLoading || isFetching ? (
            <TableSkeleton
              columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
              rows={6}
            />
          ) : data?.inventoryMovement?.length ? (
            <ListView
              data={data.inventoryMovement}
              columns={columns}
              trClassName="transition-colors duration-150"
            />
          ) : (
            <NoDataFound />
          )}
        </div>

        {/* Styled custom pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center gap-4 py-4 px-6 select-none border-t border-slate-100 animate-fadeIn">
            <div className="flex items-center gap-1">
              <Tooltip text="Previous Page">
                <button
                  onClick={() => goToPage(pageNo - 1)}
                  disabled={pageNo === 1}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                >
                  <IconChevronLeft size={14} stroke={2.5} />
                </button>
              </Tooltip>
              {renderPageNumbers().map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                  >
                    …
                  </span>
                ) : (
                  <Tooltip key={p} text={`Page ${p}`}>
                    <button
                      onClick={() => goToPage(p as number)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm ${
                        pageNo === p
                          ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {p}
                    </button>
                  </Tooltip>
                )
              )}
              <Tooltip text="Next Page">
                <button
                  onClick={() => goToPage(pageNo + 1)}
                  disabled={pageNo === totalPages}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                >
                  <IconChevronRight size={14} stroke={2.5} />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </>
  );
}
